import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaImage,
  FaChevronDown,
  FaSearch,
} from "react-icons/fa";
import useAxiosInstance from "../../hooks/useAxios";

const buildPayload = (form) => {
  const base = {
    name: form.name,
    description: form.description,
    system_prompt: form.system_prompt,
    multi_slide_prompt: form.multi_slide_prompt,
    single_slide_prompt: form.single_slide_prompt,
    image_references: form.image_references || [],
    meta: form.meta || {},
  };
  const orgIds = form.org_ids || [];
  if (orgIds.length > 0) {
    return { ...base, is_global: false, org_ids: orgIds };
  }
  return { ...base, is_global: true, org_ids: [] };
};

const emptyForm = {
  name: "",
  description: "",
  system_prompt: "",
  multi_slide_prompt: "",
  single_slide_prompt: "",
  org_ids: [],
  image_references: [],
};

function OrgPicker({ value = [], onChange, organisations }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = organisations.filter(
    (o) =>
      o.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.label?.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedOrgs = organisations.filter((o) => value.includes(o.org_id));

  const handleToggle = (orgId) => {
    if (value.includes(orgId)) {
      onChange(value.filter((id) => id !== orgId));
    } else {
      onChange([...value, orgId]);
    }
  };

  const handleRemove = (orgId) => {
    onChange(value.filter((id) => id !== orgId));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400 min-h-[34px]"
      >
        <span className={selectedOrgs.length > 0 ? "text-gray-900" : "text-gray-400"}>
          {selectedOrgs.length > 0 ? (
            <span className="flex flex-wrap items-center gap-1">
              {selectedOrgs.map((org) => (
                <span
                  key={org.org_id}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                >
                  {org.name}
                  <FaTimes
                    className="text-[8px] cursor-pointer hover:text-blue-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(org.org_id);
                    }}
                  />
                </span>
              ))}
            </span>
          ) : (
            "Global (no org)"
          )}
        </span>
        <FaChevronDown className="text-xs text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-gray-50">
              <FaSearch className="text-xs text-gray-400 shrink-0" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organisations..."
                className="flex-1 text-sm placeholder-gray-400 bg-transparent outline-none"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-52">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-center text-gray-400">
                No organisations found
              </div>
            )}
            {filtered.map((o) => {
              const isSelected = value.includes(o.org_id);
              return (
                <button
                  key={o.org_id}
                  type="button"
                  onClick={() => handleToggle(o.org_id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                    isSelected ? "bg-blue-50" : ""
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={`w-4 h-4 flex items-center justify-center border rounded ${
                        isSelected
                          ? "bg-blue-600 border-blue-600"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <FaCheck className="text-[8px] text-white" />
                      )}
                    </span>
                    <span
                      className={
                        isSelected
                          ? "text-blue-700 font-medium"
                          : "text-gray-800"
                      }
                    >
                      {o.name}
                    </span>
                  </span>
                  <span className="text-[10px] text-gray-400 shrink-0">
                    {o.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManagePPTTemplates() {
  const axiosInstance = useAxiosInstance();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [organisations, setOrganisations] = useState([]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axiosInstance.get(
        `/presentation-creator/design-templates/?is_sandbox=true`,
      );
      setTemplates(res.data);
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    fetchTemplates();
    axiosInstance
      .get("admin/organisations/")
      .then((res) => setOrganisations(res.data.organisations || []))
      .catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setCreating(true);
    try {
      await axiosInstance.post(
        `/presentation-creator/design-templates/`,
        buildPayload(createForm),
      );
      toast.success("Template created");
      setShowCreate(false);
      setCreateForm(emptyForm);
      fetchTemplates();
    } catch {
      toast.error("Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (id) => {
    try {
      await axiosInstance.put(
        `/presentation-creator/design-templates/${id}/`,
        buildPayload(editForm),
      );
      toast.success("Saved");
      setEditingId(null);
      fetchTemplates();
    } catch (error) {
      console.log(error);
      toast.error("Failed to save");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axiosInstance.delete(
        `/presentation-creator/design-templates/${deleteId}/`,
      );
      toast.success("Deleted");
      setDeleteId(null);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
      setDeleteId(null);
    }
  };

  const handleUploadRef = async (templateId, files) => {
    const uploadedUrls = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await axiosInstance.post(
          `/presentation-creator/upload-asset/`,
          fd,
        );
        console.log("[upload-asset] res.data:", res.data.results);
        // Response can be an array of {url, file_name} or a single object
        const items = Array.isArray(res.data.results)
          ? res.data.results
          : [res.data.results[0]];
        items.forEach((item) => {
          if (typeof item === "string") {
            uploadedUrls.push(item);
          } else if (item && typeof item === "object") {
            const url = item.url || item.file_url || item.path;
            if (url) uploadedUrls.push(url);
          }
        });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    console.log("[upload-asset] extracted urls:", uploadedUrls);
    if (!uploadedUrls.length) return;

    if (templateId) {
      // Editing — stage into editForm, PUT fires on save
      setEditForm((p) => ({
        ...p,
        image_references: [...(p.image_references || []), ...uploadedUrls],
      }));
    } else {
      // Creating — stage into createForm, POST fires on create
      setCreateForm((p) => ({
        ...p,
        image_references: [...(p.image_references || []), ...uploadedUrls],
      }));
    }
  };

  const handleDeleteRef = async (templateId, refId) => {
    try {
      await axiosInstance.delete(
        `/presentation-creator/design-templates/${templateId}/design-references/${refId}/`,
      );
      fetchTemplates();
    } catch {
      toast.error("Failed to delete reference");
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditForm({
      name: t.name,
      description: t.description || "",
      system_prompt: t.system_prompt || "",
      multi_slide_prompt: t.multi_slide_prompt || "",
      single_slide_prompt: t.single_slide_prompt || "",
      org_ids: t.org_ids || [],
      image_references: t.image_references || [],
      meta: t.meta || {},
    });
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-16">
        <FaSpinner className="text-xl text-gray-400 animate-spin" />
      </div>
    );

  return (
    <div className="h-screen overflow-y-auto">
      <div className="max-w-5xl p-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm text-gray-400">
          <span>Home</span>
          <span>/</span>
          <span className="text-gray-900">Manage PPT Templates</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Design Templates</h1>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800"
          >
            <FaPlus className="text-xs" />
            New Template
          </button>
        </div>

        {/* Templates List */}
        <div className="space-y-6">
          {templates.map((t) => (
            <div
              key={t.id}
              className="p-5 bg-white border border-gray-200 rounded-xl"
            >
              {/* Header row */}
              <div className="flex items-center justify-between pb-3">
                {editingId === t.id ? (
                  <div className="flex items-center flex-1 gap-2">
                    <input
                      value={editForm.name}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, name: e.target.value }))
                      }
                      className="max-w-xs px-2 py-1 text-lg font-semibold border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                    <button
                      onClick={() => handleSave(t.id)}
                      className="p-1.5 rounded hover:bg-gray-100"
                    >
                      <FaCheck className="text-sm text-green-600" />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-1.5 rounded hover:bg-gray-100"
                    >
                      <FaTimes className="text-sm text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">{t.name}</span>
                    {t.is_default && (
                      <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        DEFAULT
                      </span>
                    )}
                    {!t.is_global && t.org_ids?.length > 0 && (
                      t.org_ids.map((oid) => {
                        const org = organisations.find((o) => o.org_id === oid);
                        return (
                          <span
                            key={oid}
                            className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"
                          >
                            {org ? org.name : oid}
                          </span>
                        );
                      })
                    )}
                    {t.is_global && (
                      <span className="text-[10px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        GLOBAL
                      </span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEdit(t)}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                  >
                    <FaEdit className="text-sm" />
                  </button>
                  {!t.is_default && (
                    <button
                      onClick={() => setDeleteId(t.id)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  )}
                </div>
              </div>

              {/* Prompts */}
              <div className="space-y-4">
                {editingId === t.id ? (
                  <>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        Organisation{" "}
                        <span className="font-normal text-gray-400 normal-case">
                          (leave as global if not org-specific)
                        </span>
                      </label>
                      <OrgPicker
                        value={editForm.org_ids}
                        onChange={(v) =>
                          setEditForm((p) => ({ ...p, org_ids: v }))
                        }
                        organisations={organisations}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        Description
                      </label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            description: e.target.value,
                          }))
                        }
                        className="w-full min-h-[60px] text-sm border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        System Prompt
                      </label>
                      <textarea
                        value={editForm.system_prompt}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            system_prompt: e.target.value,
                          }))
                        }
                        className="w-full min-h-[100px] text-sm font-mono border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        Single Slide Prompt
                      </label>
                      <textarea
                        value={editForm.single_slide_prompt}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            single_slide_prompt: e.target.value,
                          }))
                        }
                        className="w-full min-h-[100px] text-sm font-mono border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        Multi-slide Prompt
                      </label>
                      <textarea
                        value={editForm.multi_slide_prompt}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            multi_slide_prompt: e.target.value,
                          }))
                        }
                        className="w-full min-h-[100px] text-sm font-mono border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {t.description && (
                      <div>
                        <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                          Description
                        </label>
                        <div className="p-3 text-sm text-gray-600 rounded-md bg-gray-50">
                          {t.description}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        System Prompt
                      </label>
                      <div className="p-3 overflow-y-auto font-mono text-xs leading-relaxed text-gray-600 rounded-md bg-gray-50 max-h-24">
                        {t.system_prompt?.slice(0, 300)}
                        {t.system_prompt?.length > 300 && "..."}
                      </div>
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        Single Slide Prompt
                      </label>
                      <div className="p-3 overflow-y-auto font-mono text-xs leading-relaxed text-gray-600 rounded-md bg-gray-50 max-h-24">
                        {t.single_slide_prompt?.slice(0, 300)}
                        {t.single_slide_prompt?.length > 300 && "..."}
                      </div>
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                        Multi-slide Prompt
                      </label>
                      <div className="p-3 overflow-y-auto font-mono text-xs leading-relaxed text-gray-600 rounded-md bg-gray-50 max-h-24">
                        {t.multi_slide_prompt?.slice(0, 300)}
                        {t.multi_slide_prompt?.length > 300 && "..."}
                      </div>
                    </div>
                  </div>
                  </>
                )}

                {/* Design References */}
                {(() => {
                  const isEditing = editingId === t.id;
                  const designRefs = t.design_references || [];
                  const imageRefs = isEditing
                    ? editForm.image_references || []
                    : t.image_references || [];
                  const hasAny = designRefs.length > 0 || imageRefs.length > 0;

                  if (!isEditing && !hasAny) return null;

                  return (
                    <div>
                      <label className="block mb-2 text-xs font-semibold text-gray-500 uppercase">
                        Design Reference Samples (
                        {designRefs.length + imageRefs.length})
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {designRefs.map((ref) => (
                          <div
                            key={ref.id}
                            className="relative w-20 h-20 overflow-hidden border border-gray-200 rounded-md group bg-gray-50"
                          >
                            {ref.thumbnail_url ? (
                              <img
                                src={ref.thumbnail_url}
                                alt={ref.filename}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full">
                                <FaImage className="text-xl text-gray-300" />
                              </div>
                            )}
                            {isEditing && (
                              <button
                                onClick={() => handleDeleteRef(t.id, ref.id)}
                                className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <FaTimes className="text-white text-[8px]" />
                              </button>
                            )}
                          </div>
                        ))}
                        {imageRefs.map((url, i) => (
                          <div
                            key={url + i}
                            className="relative w-20 h-20 overflow-hidden border border-gray-200 rounded-md group bg-gray-50"
                          >
                            <img
                              src={url}
                              alt={`ref-${i}`}
                              className="object-cover w-full h-full"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            <div className="items-center justify-center hidden w-full h-full">
                              <FaImage className="text-xl text-gray-300" />
                            </div>
                            {isEditing && (
                              <button
                                onClick={() =>
                                  setEditForm((p) => ({
                                    ...p,
                                    image_references: p.image_references.filter(
                                      (_, idx) => idx !== i,
                                    ),
                                  }))
                                }
                                className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <FaTimes className="text-white text-[8px]" />
                              </button>
                            )}
                          </div>
                        ))}
                        {isEditing && (
                          <label className="flex items-center justify-center w-20 h-20 transition-colors border-2 border-gray-200 border-dashed rounded-md cursor-pointer hover:border-gray-400">
                            <FaPlus className="text-gray-300" />
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              className="hidden"
                              onChange={(e) =>
                                e.target.files?.length &&
                                handleUploadRef(
                                  t.id,
                                  Array.from(e.target.files),
                                )
                              }
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
            <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl">
              <h2 className="mb-4 text-lg font-semibold">
                New Design Template
              </h2>
              <div className="space-y-3">
                <input
                  placeholder="Template name"
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
                <textarea
                  placeholder="Description (optional)..."
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full min-h-[60px] border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
                <div>
                  <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                    Organisation{" "}
                    <span className="font-normal text-gray-400 normal-case">
                      (leave as global if not org-specific)
                    </span>
                  </label>
                  <OrgPicker
                    value={createForm.org_ids}
                    onChange={(v) =>
                      setCreateForm((p) => ({ ...p, org_ids: v }))
                    }
                    organisations={organisations}
                  />
                </div>
                <textarea
                  placeholder="System prompt..."
                  value={createForm.system_prompt}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      system_prompt: e.target.value,
                    }))
                  }
                  className="w-full min-h-[80px] border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
                <textarea
                  placeholder="Single slide prompt..."
                  value={createForm.single_slide_prompt}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      single_slide_prompt: e.target.value,
                    }))
                  }
                  className="w-full min-h-[80px] border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
                <textarea
                  placeholder="Multi-slide prompt..."
                  value={createForm.multi_slide_prompt}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      multi_slide_prompt: e.target.value,
                    }))
                  }
                  className="w-full min-h-[80px] border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-black rounded-lg hover:bg-gray-800 disabled:bg-gray-300"
                >
                  {creating ? <FaSpinner className="animate-spin" /> : "Create"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {!!deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
            <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
              <h2 className="mb-2 text-lg font-semibold">Delete template?</h2>
              <p className="mb-4 text-sm text-gray-500">
                This will permanently delete the template and its design
                references.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
